import { getUid } from 'ol/util.js';

import { extentFromProjection, createXYZ } from 'ol/tilegrid.js';
import { getKeyZXY } from 'ol/tilecoord.js';
import { listen, unlistenByKey } from 'ol/events.js';

import * as proj from 'ol/proj.js';
import TileState from 'ol/TileState.js';
import TileSource, { TileSourceEvent } from 'ol/source/Tile.js';
import EventType from '../events/EventType.js';

import CanvasTile from '../CanvasTile.js';

const TILE_SIZE = 256;              // tile image size
const CHROME_BATCH_SIZE = 200;      // batch size for chrome rendering


const TRANSPARENT_1X1_PNG_IMAGE = new Image();
TRANSPARENT_1X1_PNG_IMAGE.src = require('../transparent_1px.png');
//TRANSPARENT_1X1_PNG_IMAGE.src = require('../coffee_24px.png');



function isChrome() {
  var isChromium = window.chrome,
    winNav = window.navigator,
    vendorName = winNav.vendor,
    isOpera = winNav.userAgent.indexOf('OPR') > -1,
    isIEedge = winNav.userAgent.indexOf('Edge') > -1,
    isIOSChrome = winNav.userAgent.match('CriOS');

  if (isIOSChrome) {
    return true;
  } else if (
    isChromium !== null &&
    typeof isChromium !== 'undefined' &&
    vendorName === 'Google Inc.' &&
    isOpera === false &&
    isIEedge === false
  ) {
    return true;
  } else {
    return false;
  }
}


//===============================================================================================
// Constructor
//===============================================================================================

/**
 * @classdesc
 * Base class for sources providing tiles divided into a tile grid over http.
 *
 * @constructor
 * @abstract
 * @fires ol.source.Tile.Event
 * @extends {ol.source.Tile}
 * @param {ol.SourceGeoJsonWorkerTileOptions} options Image tile options.
 */
function createRoundSymbol(radius, lineWidth, strokeStyle, fillStyle) {
  var offset = radius + lineWidth + 2;

  var canvasSymbol = document.createElement('canvas');
  canvasSymbol.width = offset * 2;
  canvasSymbol.height = offset * 2;

  var context = canvasSymbol.getContext('2d');

  context.lineWidth = lineWidth;
  context.strokeStyle = strokeStyle;
  context.fillStyle = fillStyle;

  context.imageSmoothingEnabled = true;
  context.mozImageSmoothingEnabled = true;
  context.webkitImageSmoothingEnabled = true;
  context.msImageSmoothingEnabled = true;

  context.beginPath();
  context.arc(offset, offset, radius, 0, 2 * Math.PI, false);
  context.fill();
  context.stroke();

  return {
    image: context.canvas,
    offsetX: -offset,
    offsetY: -offset
  }
}


class FeatureLayerWorkerTileSource extends TileSource {
  constructor(options) {
  options = options || {};
  options.transition = 0;

  super({
    attributions: options.attributions,
    cacheSize: options.cacheSize,
    extent: options.extent,
    opaque: options.opaque,
    projection: proj.get('EPSG:3857'),
    state: options.state,
    tileGrid: options.tileGrid,
    tilePixelRatio: options.tilePixelRatio,
    wrapX: options.wrapX === undefined ? true : options.wrapX,
    transition: options.transition,

    tileLoadFunction: options.tileLoadFunction ? options.tileLoadFunction : (tile) => this.defaultTileLoadFunction(tile),
  });

  /**
   * @protected
   * @type {ol.TileLoadFunctionType}
   */
  this.tileLoadFunction = options.tileLoadFunction ? options.tileLoadFunction : (tile) => this.defaultTileLoadFunction(tile);

  /**
   * @private
   * @type {int}
   */
  this._featureLayerId = null;

  /**
   * @private
   * @type {long}
   */
  this._featureLayerEpoch = null;

  /**
   * @private
   * @type {string}
   */
  this._featureGeomType = null;

  /**
   * @private
   * @type {string}
   */
  this._sessionKey = null;

  /**
   * @private
   * @type {string}
   */
  this._apiUrl = null;

  /**
   * @private
   * @type {boolean}
   */
  this._visible = false;

  /**
   * @private
   * @type {int}
   */
  this._dataCount = 0;

  /**
   * @private
   * @type {int}
   */
  this._selectedCount = 0;

  /**
   * @private
   * @type {Worker}
   */
  this._worker = null;

  /**
   * @private
   * @type {boolean}
   */
  this._workerReady = false;

  /**
   * @private
   * @type {boolean}
   */
  this._workerUpdating = false;

  /**
   * @private
   * @type {int}
   */
  this._workerRequestSeq = 1;

  /**
   * @private
   * @type {Object}
   */
  this._workerPromises = {};

  /**
   * @private
   * @type {Object}
   */
  this._workerListeners = {};

  /**
   * @private
   * @type {boolean}
   */
  this._showTileInfo = !!options.showTileInfo;

  /**
   * @private
   * @type {Object.<number, boolean>}
   */
  this._tileLoadingKeys = {};

  /**
   * @private
   * @type {function}
   */
  this._onLoadStatusChange = null;

  /**
   * @private
   * @type {array}
   */
  this._styleList = [];


  /**
   * @private
   * @type {Object}
   */
  this._defaultSymbol = null;
  
  /**
   * @private
   * @type {array}
   */
  this._symbolList = null



  var sourceProjection = this.getProjection();
  var extent = extentFromProjection(sourceProjection);

  var tileGrid = createXYZ({
    extent: extent,
    minZoom: 0,
    maxZoom: 23,
    tileSize: TILE_SIZE / (this._hidpi ? 2 : 1)
  });

  this.tileGrid = tileGrid;

  //this.setAttributions('Teste');

  this.setState('ready');
}




//===============================================================================================
// Properties
//===============================================================================================

/**
 * Return the tile load function of the source.
 * @return {ol.TileLoadFunctionType} TileLoadFunction
 * @api
 */
getTileLoadFunction() {
  return this.tileLoadFunction;
}



/**
 * Set the tile load function of the source.
 * @param {ol.TileLoadFunctionType} tileLoadFunction Tile load function.
 * @api
 */
setTileLoadFunction(tileLoadFunction) {
  this.refresh();
  this.tileLoadFunction = tileLoadFunction;
  this.changed();
}



/**
 * @inheritDoc
 */
getTile(z, x, y, pixelRatio, projection, tileCache) {
  var sourceProjection = /** @type {!ol.proj.Projection} */ (this.getProjection());

  // ol@10: cache não é mais property; guarda referência pra callbacks async.
  if (tileCache) this._lastTileCache = tileCache;

  return this.getTileInternal(z, x, y, pixelRatio, sourceProjection || projection, tileCache);
}



/**
 * @inheritDoc
 */
useTile(z, x, y, tileCache) {
  var tileCoordKey = getKeyZXY(z, x, y);
  if (tileCache && tileCache.containsKey(tileCoordKey)) {
    tileCache.get(tileCoordKey);
  }
}



/**
 * Handle tile change events.
 * @param {boolean} visible.
 * @api
 */
setVisibility(visible) {
  if (this._visible === !!visible)
    return;

  this._visible = !!visible;

  this.dispatchEvent(EventType.VISIBILITY_CHANGE);
}



/**
 * Handle tile change events.
 * @param {function} loadStatusChangeFunction.
 * @api
 */
setLoadStatusChangeFunction(loadStatusChangeFunction) {
  this._onLoadStatusChange = loadStatusChangeFunction;
}



/**
 * Checks if the worker is instantiated.
 * @return {boolean} returns true if the worker is instantiated.
 * @api
 */
initialized() {
  return !!this._worker;
}



/**
 * Checks if the layer is visible.
 * @return {boolean} returns true if the layer is visible.
 * @api
 */
visible() {
  return  this.initialized() && !!this._visible;
}



/**
 * Checks if the layer is updating.
 * @return {boolean} returns true if the layer is updating.
 * @api
 */
updating() {
  return  this._workerUpdating;
}



/**
 * Checks the number of rows.
 * @return {int} returns the number of rows.
 * @api
 */
dataCount() {
  return this._dataCount;
}



/**
 * Checks the number of selected rows.
 * @return {int} returns the number of selected rows.
 * @api
 */
selectedCount() {
  return this._selectedCount;
}



/**
 * Get the layer GeomType.
 * @return {string} returns the geometry type.
 * @api
 */
getGeomType() {
  return this._featureGeomType;
}



/**
 * Handle tile change events.
 * @param {array} list of style objects
 * @api
 */
setStyle(styleList) {
  this._styleList = styleList.concat();

  this._defaultSymbol = null;
  this._symbolList = null;


  //this.dispatchEvent(EventType.CLEAR);

  this.refresh();
}



/**
* Handle tile change events.
* @param {int} layerId Feature Layer Id to be loaded.
* @param {int} layerEpoch Feature Layer Signature epochstamp.
* @param {string} sessionKey Session key to be loaded.
* @param {string} apiUrl URL to the API.
* @api
*/
initialize(layerId, layerEpoch, geomType, sessionKey, apiUrl) {
  //console.log('initialize');

  this._featureLayerId = layerId;
  this._featureLayerEpoch = layerEpoch;
  this._featureGeomType = geomType;
  this._sessionKey = sessionKey;
  this._apiUrl = apiUrl;


  //create/reset worker
  this.initializeWorker();
}


/**
 * Clear data and terminate worker
 * @api
 */
clearData() {
  this.resetWorker();

  this.dispatchEvent(EventType.CLEAR);

  this.refresh();
}


/**
 * Clear data and terminate worker
 * @api
 */
stopDataRequests() {
  if (!this._worker)
    return false;

  if (!this._workerReady)
    return false;

  this.postWithPromise('stop_requests').then(
    (data) => {
      //console.log('stop_requests SUCCESS', data);
    },
    (data) => {
      //console.log('stop_requests ERROR', data);
    }
  );

  return true;
}


/**
 * Check and Update data.
 * @api
 */
updateData(layerEpoch, searchText, filteredColumns, theme, successCallback, errorCallback) {
  //console.log('updateData', this._workerUpdating, this._workerReady);

  if (this._workerUpdating)
    return false;

  if (!this._workerReady)
    return false;

  this._featureLayerEpoch = layerEpoch;

  this._workerUpdating = true;
  this.postWithPromise(
    'update_data',
    {
      layer_epoch: this._featureLayerEpoch,
      search_text: searchText, 
      filtered_columns: filteredColumns,
      theme: theme
    }
  ).then(
    (data) => {

      this._workerUpdating = false;
      this._dataCount = data.total;
      this._selectedCount = data.selected;

      if (data.selectionChanged)
        this.dispatchEvent(EventType.SELECTION_CHANGE);


      if (data.updates > 0 || data.deletes > 0 || data.ignored > 0 || data.restored > 0 || data.filtered || data.refresh) {
        //console.log('update_data OK:', data);
        this.dispatchEvent(EventType.CLEAR);

        this.refresh();
      }


      if (successCallback)
        successCallback();
    },
    (data) => {
      this._workerUpdating = false;

      if (!errorCallback) {
        console.log('update_data Falhou:', data);
        return;
      }

      errorCallback(data.errors && data.errors.length === 2 ? data.errors[1] : "");
    }
  );

  return true;
}


/**
 * Identify feature data that
 * @api
 */
identify(geographicExtent, resolution, limit, callback) {

  if (!this._worker)
    return false;

  if (!this._workerReady)
    return false;


  var coord1 = proj.fromLonLat([geographicExtent[0], geographicExtent[1]]);
  var coord2 = proj.fromLonLat([geographicExtent[2], geographicExtent[3]]);


  this.postWithPromise(
    'identify',
    {
      minX: coord1[0],
      minY: coord1[1],
      maxX: coord2[0],
      maxY: coord2[1],
      resolution: isNaN(resolution) ? 0 : Math.max(resolution, 0),
      limit: isNaN(limit) ? Number.MAX_VALUE : Math.max(Math.floor(limit), 0)
    }
  ).then(
    (data) => {
      //console.log('identify OK', data);

      var idData = new Uint32Array(data.idBuffer);
      
      callback( Array.from(idData) );
    },
    (data) => {
      console.log('identify Falhou', data);

      callback([]);
    }
  );

  return true;
}



/**
 * select feature data
 * @api
 */
select(idList) {

  if (!this._worker)
    return false;

  if (!this._workerReady)
    return false;

  if (!idList || !idList.length)
    return false;

  this.postWithPromise(
    'select',
    {
      idList: idList
    }
  ).then(
    (data) => {

      if (data.changed) {
        this._selectedCount = data.selected;

        this.dispatchEvent(EventType.SELECTION_CHANGE);
      }
    },
    (data) => {
      console.log('select Falhou', data);
    }
  );

  return true;
}



/**
 * deselect feature data
 * @api
 */
deselect(idList) {

  if (!this._worker)
    return false;

  if (!this._workerReady)
    return false;

  if (!idList || !idList.length)
    return false;

  this.postWithPromise(
    'deselect',
    {
      idList: idList
    }
  ).then(
    (data) => {

      if (data.changed) {
        this._selectedCount = data.selected;

        this.dispatchEvent(EventType.SELECTION_CHANGE);
      }
    },
    (data) => {
      console.log('deselect Falhou', data);
    }
  );

  return true;
}



/**
 * select feature data that
 * @api
 */
getSelectedIds(idList, callback) {

  if (!this._worker)
    return false;

  if (!this._workerReady)
    return false;


  this.postWithPromise(
    'get_selected_ids',
    {
      idList: idList || []
    }
  ).then(
    (data) => {
      var idData = new Uint32Array(data.idBuffer);
      
      callback( Array.from(idData) );
    },
    (data) => {
      console.log('get_selected_ids Falhou', data);

      callback([]);
    }
  );

  return true;
}



/**
 * select feature data that
 * @api
 */
changeSelection(geographicExtent, resolution) {

  if (!this._worker)
    return false;

  if (!this._workerReady)
    return false;

  var coord1 = proj.fromLonLat([geographicExtent[0], geographicExtent[1]]);
  var coord2 = proj.fromLonLat([geographicExtent[2], geographicExtent[3]]);

  this.postWithPromise(
    'change_selection',
    {
      minX: coord1[0],
      minY: coord1[1],
      maxX: coord2[0],
      maxY: coord2[1],
      resolution: resolution
    }
  ).then(
    (data) => {

      if (data.changed) {
        this._selectedCount = data.selected;

        this.dispatchEvent(EventType.SELECTION_CHANGE);
        //this.dispatchEvent(EventType.CLEAR);

        //this.tileCache.clear();
        //this.changed();
      }


      //console.log('change_selection OK', data);

    },
    (data) => {
      console.log('change_selection Falhou', data);
    }
  );

  return true;
}



/**
 * deselect feature data
 * @api
 */
clearSelection() {

  if (!this._worker)
    return false;

  if (!this._workerReady)
    return false;

  this.postWithPromise(
    'clear_selection'
  ).then(
    (data) => {
      this._selectedCount = 0;

      if (data.changed) {

        this.dispatchEvent(EventType.SELECTION_CHANGE);
        //this.dispatchEvent(EventType.CLEAR);

        //this.tileCache.clear();
        //this.changed();
      }

      //console.log('clear_selection OK', changed);

    },
    (data) => {
      console.log('clear_selection Falhou', data);
    }
  );

  return true;
}


/**
 * Checks the number of selected rows.
 * @return {boolean} returns the number of selected rows.
 * @api
 */
updateSelectedCount() {
  if (!this._worker) {
    this._selectedCount = 0;
    return false;
  }

  if (!this._workerReady) {
    this._selectedCount = 0;
    return false;
  }

  this.postWithPromise(
    'get_selection_count'
  ).then(
    (data) => {
      this._selectedCount = data;
    },
    (data) => {
      console.log('get_selection_count Falhou', data);
    }
  );

  return true;
}


/**
 * Get Feature Data Row by id.
 * @api
 */
getFeatureDataById(id, callback) {

  if (!this._worker) {
    callback(null);
    return;
  }

  if (!this._workerReady) {
    callback(null);
    return;
  }


  this.postWithPromise(
    'get_feature',
    {
      id: id
    }
  ).then(
    (data) => {
      //console.log('get_feature OK', data);
      callback(data.geojson);
    },
    (data) => {
      //console.log('get_feature Falhou', data);
      callback(null);
    }
  );
}



//===============================================================================================
// Worker
//===============================================================================================

/**
 * Worker disposer.
 * @protected
 */
resetWorker() {
  if (this._worker) {
    this._workerReady = false;
    this._workerUpdating = false;
    //this._worker.postMessage({ uid: 1, action: 'stop' });
    this._worker.terminate();

    this._worker = null;

    this._workerListeners = {};
  }

  this._dataCount = 0;
  this._selectedCount = 0;
  this.setLoadStatus(0, 0, 0);
}



/**
* Worker initializer.
* @protected
*/
initializeWorker() {

  this.resetWorker();
  this.setLoadStatus(-1, -1, -1);

  //this._worker = new Worker('./assets/geocloud2/featureLayerTileWorker.js');
  this._worker = new Worker('./assets/geocloud2/featureLayerTileWorker.min.js');

  this._worker.onerror = (event) => {
    console.log("Error loading worker!");
    this.resetWorker();
  }

  this._worker.onmessage = (event) => {
    var data = event.data;

    if (data.success) {
      if (data.uid !== null) {
        if (this._workerPromises[data.uid]) {
          this._workerPromises[data.uid].resolve(data.response);
          delete this._workerPromises[data.uid];
        }
      }
      else if (this._workerListeners[data.action]) {
        this._workerListeners[data.action].forEach(listener => {
          listener(data.response);
        });
      }
    }
    else {
      if (data.uid !== null) {
        if (this._workerPromises[data.uid]) {
          this._workerPromises[data.uid].reject(data.response);
          delete this._workerPromises[data.uid];
        }
      }
      else if (this._workerListeners[data.action]) {
        this._workerListeners[data.action].forEach(listener => {
          listener(data.response);
        });
      }
    }
  }


  this.addWorkerListener('batch_load_status', (data) => {
    this.setLoadStatus(data[0], data[1], data[2]);
  });

  this.postWithPromise(
    'initialize',
    {
      api_url: this._apiUrl,
      layer_id: this._featureLayerId,
      layer_epoch: this._featureLayerEpoch,
      geom_type: this._featureGeomType,
      session_key: this._sessionKey
    }
  ).then(
    (data) => {
      //this._workerReady = true;

      //if (successCallback)
      //    successCallback();
    },
    (data) => {
      //this._workerReady = true;

      //if (errorCallback)
      //    errorCallback( data.errors && data.errors.length>0 ? data.errors[0].Code : "");
    }
  );

  this._workerReady = true;



  //redraw
  this.dispatchEvent(EventType.CLEAR);

  this.refresh();
}


/**
 * Handle tile change events.
 * @param {string} action action string.
 * @param {object} params object params.
 * @protected
 */
setLoadStatus(queuePos, queueTotal, affectedRows) {
  //console.log('batch_load_status:', queuePos, queueTotal, affectedRows);

  if (affectedRows < 0) {
    //console.log('batch_load_status:', 'start loading');
    this._loadStatus = 0;
    this._affectedRows = -1;
  }
  else {
    this._loadStatus = affectedRows > 0 ? queuePos / queueTotal : 1;
    this._affectedRows = affectedRows;
  }

  //console.log('batch_load_status:', Math.round(10000 * this._loadStatus)/100 +'% - '+ this._affectedRows +' rows');

  if (this._onLoadStatusChange)
    this._onLoadStatusChange(this._loadStatus, this._affectedRows);
}

/**
 * Handle tile change events.
 * @param {string} action action string.
 * @param {object} params object params.
 * @protected
 */
postWithPromise(action, params) {
  return new Promise((resolve, reject) => {
    if (!this._worker) {
      reject();
    }

    var id = this._workerRequestSeq++;
    //msgid
    this._worker.postMessage({ uid: id, action: action, params: params });

    this._workerPromises[id] = { resolve: resolve, reject: reject };
  });
}

/**
 * Handle tile change events.
 * @param {string} action action string.
 * @param {function} listener callback listener.
 * @protected
 */
addWorkerListener(action, listener) {
  if (!this._workerListeners[action])
    this._workerListeners[action] = [];

  this._workerListeners[action].push(listener);
}

/**
 * Handle tile change events.
 * @param {string} action action string.
 * @param {function} listener callback listener.
 * @protected
 */
removeWorkerListener(action, listener) {
  if (!this._workerListeners[action])
    return;
  var idx = this._workerListeners[action].indexOf(listener);

  if (idx >= 0)
    this._workerListeners[action].splice(idx, 1);
}



//===============================================================================================
// Event Handlers
//===============================================================================================


/**
 * Handle tile change events.
 * @param {ol.events.Event} event Event.
 * @protected
 */
handleTileChange(event) {
  var tile = /** @type {ol.Tile} */ (event.target);
  var uid = getUid(tile);
  var tileState = tile.getState();
  var type;
  if (tileState === TileState.LOADING) {
    this._tileLoadingKeys[uid] = true;
    type = 'tileloadstart';
  } else if (uid in this._tileLoadingKeys) {
    delete this._tileLoadingKeys[uid];
    type = tileState === TileState.ERROR ? 'tileloaderror' :
      (tileState === TileState.LOADED || tileState === TileState.ABORT) ?
        'tileloadend' : undefined;
  }
  if (type !== undefined) {
    this.dispatchEvent(new TileSourceEvent(type, tile));
  }

}




//===============================================================================================
// Methods
//===============================================================================================


/**
 * @param {ol.CanvasTile} canvasTile Canvas tile.
 * @private
 */
_renderTileInfo(canvasTile) {
  if (!this._showTileInfo)
    return;

  var context = canvasTile.getImage().getContext('2d');

  var tileCoord = canvasTile.getTileCoord();
  let urlTileCoord = this._generateUrlTileCoord(tileCoord);

  context.strokeStyle = 'black';
  context.fillStyle = 'rgba(0,0,0,1)';
  context.font = '20px Georgia';

  context.fillText(canvasTile.getTileId(), 10, 50);
  context.fillText(' (' + tileCoord + ')', 10, 75);
  context.fillText(' [' + urlTileCoord + ']', 10, 105);
}



/**
 * @param {ol.CanvasTile} canvasTile Canvas tile.
 * @return {boolean} loaded.
 * @protected
 */
defaultTileLoadFunction(canvasTile) {
  return this._renderTileIndexContent(canvasTile);
}



/**
 * @param {number} z Tile coordinate z.
 * @param {number} x Tile coordinate x.
 * @param {number} y Tile coordinate y.
 * @param {number} pixelRatio Pixel ratio.
 * @param {!ol.proj.Projection} projection Projection.
 * @return {!ol.Tile} Tile.
 * @protected
 */
getTileInternal(z, x, y, pixelRatio, projection, tileCache) {
  var tile = null;
  var tileCoordKey = getKeyZXY(z, x, y);

  //var sourceKey = this.getKey();
  var sourceKey = 'CHAVE_DE_VERSAO';

  if (!tileCache || !tileCache.containsKey(tileCoordKey)) {
    tile = this._createTile(z, x, y, pixelRatio, projection, sourceKey);

    if (tileCache) tileCache.set(tileCoordKey, tile);
  }
  else {
    tile = tileCache.get(tileCoordKey);

    if (tile.key !== sourceKey) {
      // The source's params changed. If the tile has an interim tile and if we
      // can use it then we use it. Otherwise we create a new tile.  In both
      // cases we attempt to assign an interim tile to the new tile.
      var interimTile = tile;
      tile = this._createTile(z, x, y, pixelRatio, projection, sourceKey);

      //make the new tile the head of the list,
      if (interimTile.getState() === TileState.IDLE) {
        //the old tile hasn't begun loading yet, and is now outdated, so we can simply discard it
        tile.interimTile = interimTile.interimTile;
      } else {
        tile.interimTile = interimTile;
      }
      tile.refreshInterimChain();
      tileCache.replace(tileCoordKey, tile);
    }
  }

  return tile;
}



/**
 * @param {number} z Tile coordinate z.
 * @param {number} x Tile coordinate x.
 * @param {number} y Tile coordinate y.
 * @param {number} pixelRatio Pixel ratio.
 * @param {string} sourceKey The source key set on the tile.
 * @return {!ol.Tile} Tile.
 * @private
 */
_createTile(z, x, y, pixelRatio, projection, sourceKey) {
  var tileCoord = [z, x, y];
  var withinExtent = this.getTileCoordForTileUrlFunction(tileCoord, projection);

  var tileState = withinExtent ? TileState.IDLE : TileState.EMPTY;

  var tile = new CanvasTile(
    tileCoord,
    tileState,
    this.tileLoadFunction,
    true, //wait load
    this.tileOptions);

  tile.key = sourceKey;

  listen(tile, EventType.CHANGE, this.handleTileChange, this);

  return tile;
}


/**
 * @param {!ol.Tile} canvasTile The Tile.
 * @return {boolean} Returns true if any content is rendered, else false.
 * @private
 */
_renderTileIndexContent(canvasTile) {


  if (!this._worker)
    return true;

  if (!this._workerReady)
    return true;


  let tileCoord = canvasTile.getTileCoord();
  let urlTileCoord = this._generateUrlTileCoord(tileCoord);

  //console.time('('+ urlTileCoord +')')

  this.postWithPromise(
    'get_tile',
    {
      Z: urlTileCoord[0],
      X: urlTileCoord[1],
      Y: urlTileCoord[2],
      filter: ""
    }
  ).then(
    (data) => {
      //console.log('get_tile OK (' + data + ')');
      //console.timeEnd('('+ urlTileCoord +')')

      if (canvasTile.state === TileState.ERROR) {
        //console.log('TILE_RESSURRECT: '+ canvasTile.getTileId());
        canvasTile.reset();
      }

      if (canvasTile.state !== TileState.LOADING && canvasTile.state !== TileState.IDLE) {
        //console.log('TILE_STATE: '+ tileCoordKey +' - '+ canvasTile.state);
        return;
      }

      if (this._renderTileContent(canvasTile, data.type, data.geometryBuffer, data.styleBuffer)) {
        canvasTile.setLoadSuccess();
        //console.log('LOAD_SUCCESS: '+ tileCoordKey);
      }
      else {
        canvasTile.setLoadError();
      }
    },
    (data) => {
      console.log('get_tile Falhou (' + data + ')');

      if (canvasTile.state === TileState.ERROR) {
        return;
      }

      if (canvasTile.state !== TileState.LOADING) {
        //console.log('TILE_STATE: '+ tileCoordKey +' - '+ canvasTile.state);
        return;
      }

      canvasTile.setLoadError();
    }
  );

  return false;
}


/**
 * @param {!ol.Tile} canvasTile The Tile.
 * @param {object} tileData The data to be rendered.
 * @return {boolean} Returns true if any content is rendered, else false.
 * @private
 */
_renderTileContent(canvasTile, type, geometryBuffer, styleBuffer) {

  var linearData = type === 'int16' ? new Int16Array(geometryBuffer) : new Float32Array(geometryBuffer);
  var styleData = new Int8Array(styleBuffer);


  if (!linearData.length)
    return false;


  var context = canvasTile.getImage().getContext('2d');

  context.imageSmoothingEnabled = true;
  context.mozImageSmoothingEnabled = true;
  context.webkitImageSmoothingEnabled = true;
  context.msImageSmoothingEnabled = true;

  //context.lineCap="round";
  //context.lineJoin="round";
  context.miterLimit = 2;

  context.save();

  context.clearRect(0, 0, TILE_SIZE, TILE_SIZE);

  //Feature type
  // 1 - point
  // 2 - line
  // 3 - polygon

  switch (linearData[0]) {
    case 1:
      // 1 - point
      this._renderPoints(context, linearData, styleData);
      break;

    case 2:
      // 2 - line
      if (isChrome())
        this._renderLinesForChrome(context, linearData, styleData);
      else
        this._renderLines(context, linearData, styleData);
      break;

    case 3:
      // 3 - polygon
      this._renderPolygons(context, linearData, styleData);
      break;

    default:
      break;
  }


  context.restore();


  this._renderTileInfo(canvasTile);

  return true;
}



/**
* @private
*/
_renderPoints(context, linearData, styleData) {

  if (!this._defaultSymbol) {
    this._symbolList = this._styleList.map(x => x.symbol);

    //Default Symbol
    //this._defaultSymbol = createRoundSymbol(2, 1, 'green', 'yellow');
    this._defaultSymbol = createRoundSymbol(5, 1, 'rgba(0,99,99, 0.8)', '#00FFFF');
  }

  //increases render performance on Chrome
  context.drawImage(TRANSPARENT_1X1_PNG_IMAGE, 0, 0);


  var lastStyle = -1;
  var currSymbol = this._defaultSymbol;


  // pattern
  // [featureType, x1, y1, x2, ..., xN, yN]

  var idx = 1;
  var qtd = linearData.length;

  var count = 0;
  while (idx < qtd) {
    if (lastStyle !== styleData[count]) {
      lastStyle = styleData[count];

      currSymbol = this._symbolList[lastStyle] || this._defaultSymbol;
    }

    var px = linearData[idx++] + currSymbol.offsetX;
    var py = linearData[idx++] + currSymbol.offsetY;

    //already rounded
    //  px = Math.round(px);
    //  py = Math.round(py);

    context.drawImage(currSymbol.image, px, py);

    count++;
  }

}


/**
 * @private
 */
_renderLinesForChrome(context, linearData, styleData) {

  var styleList = this._styleList;

  var defaultStyle = {
    lineWidth: 3,
    strokeStyle: "#00FFFF"
  }

  var styleCount = styleList.length;
  var lastStyle = -99999;

  var path2d = new Path2D();
  var renderCheck = CHROME_BATCH_SIZE;

  // pattern
  // [featureType, pathSize, x1, y1, x2, ..., xN, yN,  pathSize, x1, y1, x2, ...]

  var idx = 1;
  var qtd = linearData.length;

  var count = 0;
  while (idx < qtd) {

    if (lastStyle !== styleData[count]) {

      if (renderCheck < CHROME_BATCH_SIZE) {
        context.stroke(path2d);

        path2d = new Path2D();
        renderCheck = CHROME_BATCH_SIZE;
      }

      lastStyle = styleData[count];

      if (lastStyle < 0 || lastStyle >= styleCount) {
        context.lineWidth = defaultStyle.lineWidth;
        context.strokeStyle = defaultStyle.strokeStyle;
        //context.fillStyle = defaultStyle.fillStyle;
      }
      else {
        context.lineWidth = styleList[lastStyle].lineWidth;
        context.strokeStyle = styleList[lastStyle].strokeStyle;
        //context.fillStyle = styleList[lastStyle].fillStyle;
      }
    }


    var coordCount = linearData[idx++];

    var px = linearData[idx++];
    var py = linearData[idx++];

    path2d.moveTo(px, py);

    for (var k = 1; k < coordCount; k++) {
      px = linearData[idx++];
      py = linearData[idx++];

      path2d.lineTo(px, py);
    }

    if (--renderCheck <= 0) {
      context.stroke(path2d);

      path2d = new Path2D();
      renderCheck = CHROME_BATCH_SIZE;
    }

    count++;
  }

  if (renderCheck < CHROME_BATCH_SIZE) {
    context.stroke(path2d);
  }
}


/**
 * @private
 */
_renderLines(context, linearData, styleData) {

  var styleList = this._styleList;

  var defaultStyle = {
    lineWidth: 3,
    strokeStyle: "#00FFFF"
  }

  var styleCount = styleList.length;
  var lastStyle = -99999;

  // pattern
  // [featureType, pathSize, x1, y1, x2, ..., xN, yN,  pathSize, x1, y1, x2, ...]

  var idx = 1;
  var qtd = linearData.length;

  var count = 0;
  while (idx < qtd) {

    if (lastStyle !== styleData[count]) {
      lastStyle = styleData[count];

      if (lastStyle < 0 || lastStyle >= styleCount) {
        context.lineWidth = defaultStyle.lineWidth;
        context.strokeStyle = defaultStyle.strokeStyle;
        //context.fillStyle = defaultStyle.fillStyle;
      }
      else {
        context.lineWidth = styleList[lastStyle].lineWidth;
        context.strokeStyle = styleList[lastStyle].strokeStyle;
        //context.fillStyle = styleList[lastStyle].fillStyle;
      }
    }


    context.beginPath();

    var coordCount = linearData[idx++];

    var px = linearData[idx++];
    var py = linearData[idx++];

    context.moveTo(px, py);

    for (var k = 1; k < coordCount; k++) {
      px = linearData[idx++];
      py = linearData[idx++];

      context.lineTo(px, py);
    }

    context.stroke();

    count++;
  }
}


/**
 * @private
 */
_renderPolygons(context, linearData, styleData) {

  var styleList = this._styleList;

  var defaultStyle = {
    lineWidth: 3,
    strokeStyle: "#00FFFF",
    fillStyle: "rgba(0,255,255,0.2)"
  }

  var styleCount = styleList.length;
  var lastStyle = -99999;

  // pattern
  // [featureType, featureCount, ringCount, ringSize, ring1_x1, ring1_y1, ring1_x2, ..., ring1_xN, ring1_yN,  ringSize, ring2_x1, ring2_y1, ring2_x2, ...]

  var idx = 1;
  var qtd = linearData.length;

  var count = 0;
  while (idx < qtd) {

    if (lastStyle !== styleData[count]) {
      lastStyle = styleData[count];

      if (lastStyle < 0 || lastStyle >= styleCount) {
        context.lineWidth = defaultStyle.lineWidth;
        context.strokeStyle = defaultStyle.strokeStyle;
        context.fillStyle = defaultStyle.fillStyle;
      }
      else {
        context.lineWidth = styleList[lastStyle].lineWidth;
        context.strokeStyle = styleList[lastStyle].strokeStyle;
        context.fillStyle = styleList[lastStyle].fillStyle;
      }
    }


    context.beginPath();
    var ringCount = linearData[idx++];

    for (var j = 0; j < ringCount; j++) {
      var coordCount = linearData[idx++];

      var px = linearData[idx++];
      var py = linearData[idx++];

      context.moveTo(px, py);

      for (var k = 1; k < coordCount; k++) {
        px = linearData[idx++];
        py = linearData[idx++];

        context.lineTo(px, py);
      }
    }

    count++;

    context.closePath();

    context.fill('evenodd');
    context.stroke();
  }
}





//===============================================================================================
// Utilitary
//===============================================================================================

/**
 * @param {string} sourceKey The source key set on the tile.
 * @param {number} z Tile coordinate z.
 * @param {number} x Tile coordinate x.
 * @param {number} y Tile coordinate y.
 * @return {string} tile key.
 * @private
 */
_generateUrlTileKey(sourceKey, z, x, y) {
  return sourceKey + '_' + z + '_' + x + '_' + y;
}

/**
 * @param {number[]} [z, x, y] number array
 * @return {number[]} [z, x, y] number array
 * @private
 */
_generateUrlTileCoord(tileCoord) {
  var wrappedCoord = this.getTileCoordForTileUrlFunction(tileCoord);

  return [wrappedCoord[0], wrappedCoord[1], -wrappedCoord[2] - 1];
}





}

export default FeatureLayerWorkerTileSource;